/**
 * ReferencePhotoQualityHistory — 참조사진 품질 검수 히스토리 (임계값 튜닝 근거)
 *
 * "참조사진 품질" 페이지(ReferencePhotoQualitySettings)의 "검수 히스토리" 탭 본문.
 * GET /api/admin/ai-reference-photos (+?include=distribution) 를 소비한다.
 *
 * 네 가지 뷰:
 *  1. 판정 분포 요약 — StatCard + DonutChart (ok/warn/block)
 *  2. 필터 바 — 검색(이메일/이름=사용자 검색)·역할·판정·기간
 *  3. 개별 레코드 테이블 — 업로드별 지표 수치 + 판정 + [보기]
 *  4. 지표별 값 분포 — 지표 6종 히스토그램 + 현재 임계값 경계 표기
 *
 * block 판정 row 는 원본을 보관하지 않으므로(originalUrl null) [보기] 불가.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useAdminFilters } from '@/hooks/useAdminFilters'
import { formatDate, formatNumber } from '@/ui/format'
import {
  Alert, Badge, BarChart, Button, DatePicker, DonutChart, Input, Modal,
  Pagination, Select, StatCard, Table, Tooltip, ViewReasonModal,
  type BarDatum, type TableColumn,
} from '@/ui'
import type { Thresholds } from './ReferencePhotoQualitySettings'
import refStyles from './AIReferencePhotos.module.css'
import styles from './ReferencePhotoQualityHistory.module.css'

// --- 타입 ---

type Verdict = 'ok' | 'warn' | 'block'

interface Metrics {
  confidence: number | null
  yaw: number | null
  pitch: number | null
  roll: number | null
  brightness: number | null
  sharpness: number | null
  faceSize: number | null
}

interface RefPhotoRow {
  id: string
  userId: string | null
  role: string
  faceDetected: boolean
  qualityVerdict: Verdict | null
  metrics: Metrics
  qualityWarnings: string[]
  qualityBlocks: string[]
  isActive: boolean
  originalUrl?: string | null
  createdAt: string
  user: { email: string; name: string | null }
}

interface HistoryResponse {
  photos: RefPhotoRow[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
  stats: {
    totalCount: number
    activeCount: number
    groomCount: number
    brideCount: number
    faceDetectedCount: number
    uniqueUsers: number
    okCount: number
    warnCount: number
    blockCount: number
  }
  distribution?: Record<string, BarDatum[]>
}

interface ViewPhoto {
  id: string
  role: string
  originalUrl: string
  faceDetected: boolean
  isActive: boolean
  createdAt: string
}

// --- 상수 ---

const VERDICT_META: Record<Verdict, { label: string; color: 'green' | 'yellow' | 'red' }> = {
  ok: { label: '통과', color: 'green' },
  warn: { label: '경고', color: 'yellow' },
  block: { label: '거부', color: 'red' },
}
const VERDICT_HEX: Record<Verdict, string> = { ok: '#16a34a', warn: '#eab308', block: '#ef4444' }

const ROLE_OPTIONS = [
  { value: '', label: '전체 역할' },
  { value: 'GROOM', label: '신랑' },
  { value: 'BRIDE', label: '신부' },
]

const VERDICT_OPTIONS = [
  { value: '', label: '전체 판정' },
  { value: 'ok', label: '통과' },
  { value: 'warn', label: '경고' },
  { value: 'block', label: '거부' },
]

// 지표별 차트 설정 (label + 현재 임계값에서 경계선을 뽑는 함수)
interface MetricChart {
  key: keyof Metrics
  label: string
  bounds: (t: Thresholds) => string
}
const METRIC_CHARTS: MetricChart[] = [
  { key: 'yaw', label: '좌우 각도 |yaw|', bounds: (t) => `warn ${t.yaw.warn} / block ${t.yaw.block}` },
  { key: 'pitch', label: '상하 각도 |pitch|', bounds: (t) => `warn ${t.pitch.warn} / block ${t.pitch.block}` },
  { key: 'roll', label: '기울기 |roll|', bounds: (t) => `warn ${t.roll.warn} / block ${t.roll.block}` },
  { key: 'brightness', label: '밝기 brightness', bounds: (t) => `block ${t.brightness.blockLow} / warn ${t.brightness.warnLow}~${t.brightness.warnHigh}` },
  { key: 'sharpness', label: '초점 sharpness', bounds: (t) => `block ${t.sharpnessMin.block} / warn ${t.sharpnessMin.warn} 미만` },
  { key: 'faceSize', label: '얼굴 크기 faceSize', bounds: (t) => `block ${t.faceSizeMin.block} / warn ${t.faceSizeMin.warn} 미만` },
]

// --- 컴포넌트 ---

export function ReferencePhotoQualityHistory({ thresholds }: { thresholds: Thresholds }) {
  const { filters, setFilters, resetFilters } = useAdminFilters({ pageSize: 50 })

  const [roleFilter, setRoleFilter] = useState('')
  const [verdictFilter, setVerdictFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const [reasonTarget, setReasonTarget] = useState<{ userId: string; label: string } | null>(null)
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number; label: string } | null>(null)

  const viewMutation = useApiMutation<{ photos: ViewPhoto[] }, { userId: string; reason: string }>(
    '/api/admin/ai-reference-photos/view',
  )

  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  // 300ms debounce
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(searchInput)
      setFilters({ page: 1 })
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput, setFilters])

  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(filters.page))
    p.set('pageSize', String(filters.pageSize))
    p.set('sortBy', filters.sortBy)
    p.set('sortOrder', filters.sortOrder)
    // isActive 는 보내지 않는다 → 활성/비활성 모두 포함 (block row 는 비활성)
    p.set('include', 'distribution')
    if (roleFilter) p.set('role', roleFilter)
    if (verdictFilter) p.set('verdict', verdictFilter)
    if (searchDebounced) p.set('search', searchDebounced)
    if (startDate) p.set('startDate', new Date(startDate).toISOString())
    if (endDate) p.set('endDate', new Date(endDate + 'T23:59:59').toISOString())
    return p.toString()
  }, [filters, roleFilter, verdictFilter, searchDebounced, startDate, endDate])

  const { data, isLoading, error } = useApiFetch<HistoryResponse>(
    `/api/admin/ai-reference-photos?${params}`,
  )

  const handleResetFilters = useCallback(() => {
    setRoleFilter('')
    setVerdictFilter('')
    setSearchInput('')
    setSearchDebounced('')
    setStartDate('')
    setEndDate('')
    resetFilters()
  }, [resetFilters])

  // 라이트박스 키보드 네비
  useEffect(() => {
    if (!lightbox) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') setLightbox((prev) => (prev ? { ...prev, index: (prev.index + 1) % prev.urls.length } : prev))
      else if (e.key === 'ArrowLeft') setLightbox((prev) => (prev ? { ...prev, index: (prev.index - 1 + prev.urls.length) % prev.urls.length } : prev))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightbox])

  const openReason = (row: RefPhotoRow) => {
    if (!row.userId) return
    viewMutation.reset()
    setReasonTarget({ userId: row.userId, label: row.user.name ?? row.user.email ?? '익명' })
  }

  const handleConfirmView = async (reason: string) => {
    if (!reasonTarget) return
    const res = await viewMutation.trigger({ userId: reasonTarget.userId, reason })
    if (res?.photos) {
      setLightbox({ urls: res.photos.map((p) => p.originalUrl), index: 0, label: reasonTarget.label })
      setReasonTarget(null)
      viewMutation.reset()
    }
  }

  const stats = data?.stats
  const donutData: BarDatum[] = useMemo(() => {
    if (!stats) return []
    return [
      { label: '통과', value: stats.okCount, color: VERDICT_HEX.ok },
      { label: '경고', value: stats.warnCount, color: VERDICT_HEX.warn },
      { label: '거부', value: stats.blockCount, color: VERDICT_HEX.block },
    ].filter((d) => d.value > 0)
  }, [stats])

  const blockRate = stats && stats.totalCount > 0
    ? Math.round((stats.blockCount / stats.totalCount) * 100)
    : 0

  const fmtMetric = (v: number | null, digits = 1) => (v == null ? '—' : v.toFixed(digits))

  const columns: TableColumn<RefPhotoRow>[] = [
    {
      key: 'user',
      label: '유저',
      render: (r) => (
        <div className={refStyles.userCell}>
          <span className={refStyles.userName}>{r.user.name ?? r.user.email ?? '익명'}</span>
          {r.user.email && r.user.name && <span className={refStyles.userEmailDim}>{r.user.email}</span>}
        </div>
      ),
    },
    {
      key: 'role',
      label: '역할',
      width: '70px',
      render: (r) => (r.role === 'GROOM' ? <Badge color="blue">신랑</Badge> : <Badge color="green">신부</Badge>),
    },
    {
      key: 'verdict',
      label: '판정',
      width: '80px',
      render: (r) => {
        const v = r.qualityVerdict
        if (!v) return <span className={refStyles.userEmailDim}>—</span>
        return <Badge color={VERDICT_META[v].color}>{VERDICT_META[v].label}</Badge>
      },
    },
    { key: 'confidence', label: '확신도', width: '70px', align: 'right', render: (r) => fmtMetric(r.metrics.confidence, 0) },
    { key: 'yaw', label: 'yaw', width: '60px', align: 'right', render: (r) => fmtMetric(r.metrics.yaw) },
    { key: 'pitch', label: 'pitch', width: '60px', align: 'right', render: (r) => fmtMetric(r.metrics.pitch) },
    { key: 'roll', label: 'roll', width: '60px', align: 'right', render: (r) => fmtMetric(r.metrics.roll) },
    { key: 'brightness', label: '밝기', width: '60px', align: 'right', render: (r) => fmtMetric(r.metrics.brightness, 0) },
    { key: 'sharpness', label: '초점', width: '60px', align: 'right', render: (r) => fmtMetric(r.metrics.sharpness, 0) },
    { key: 'faceSize', label: '얼굴크기', width: '70px', align: 'right', render: (r) => fmtMetric(r.metrics.faceSize, 2) },
    {
      key: 'reasons',
      label: '사유',
      width: '140px',
      render: (r) => {
        const reasons = [...r.qualityBlocks, ...r.qualityWarnings]
        if (reasons.length === 0) return <span className={refStyles.userEmailDim}>—</span>
        return <span className={styles.reasons}>{reasons.join(', ')}</span>
      },
    },
    { key: 'createdAt', label: '시각', width: '130px', render: (r) => formatDate(r.createdAt, 'yyyy.M.d HH:mm') },
    {
      key: 'action',
      label: '',
      width: '70px',
      align: 'right',
      render: (r) => {
        // block row 는 원본 미보관(originalUrl null) → 열람 불가
        const canView = !!r.userId && r.qualityVerdict !== 'block'
        const btn = (
          <Button size="sm" variant="outline" onClick={() => openReason(r)} disabled={!canView}>
            보기
          </Button>
        )
        if (canView) return btn
        return <Tooltip content="거부된 사진은 원본이 보관되지 않습니다">{btn}</Tooltip>
      },
    },
  ]

  return (
    <div className={styles.root}>
      {/* 1. 판정 분포 요약 */}
      <div className={styles.summaryRow}>
        <div className={refStyles.statGrid}>
          <StatCard label="총 검수 건수" value={formatNumber(stats?.totalCount ?? 0)} />
          <StatCard label="통과 (ok)" value={formatNumber(stats?.okCount ?? 0)} />
          <StatCard label="경고 (warn)" value={formatNumber(stats?.warnCount ?? 0)} />
          <StatCard label="거부 (block)" value={formatNumber(stats?.blockCount ?? 0)} />
          <StatCard label="거부율" value={`${blockRate}%`} />
          <StatCard label="고유 유저 수" value={formatNumber(stats?.uniqueUsers ?? 0)} />
        </div>
        {donutData.length > 0 && (
          <div className={styles.donutWrap}>
            <DonutChart data={donutData} showLegend showTooltip />
          </div>
        )}
      </div>

      {/* 2. 필터 바 */}
      <div className={refStyles.filterBar}>
        <Input
          size="sm"
          placeholder="유저 이메일/이름 검색"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className={refStyles.search}
        />
        <Select size="sm" value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setFilters({ page: 1 }) }} options={ROLE_OPTIONS} />
        <Select size="sm" value={verdictFilter} onChange={(e) => { setVerdictFilter(e.target.value); setFilters({ page: 1 }) }} options={VERDICT_OPTIONS} />
        <DatePicker
          size="sm"
          value={startDate}
          onChange={(_d: Date | null, formatted: string) => { setStartDate(formatted); setFilters({ page: 1 }) }}
          placeholder="시작일"
        />
        <DatePicker
          size="sm"
          value={endDate}
          onChange={(_d: Date | null, formatted: string) => { setEndDate(formatted); setFilters({ page: 1 }) }}
          placeholder="종료일"
        />
        <Button size="sm" variant="ghost" onClick={handleResetFilters}>초기화</Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* 3. 개별 레코드 테이블 */}
      <Table
        columns={columns}
        data={data?.photos ?? []}
        rowKey={(r) => r.id}
        isLoading={isLoading && !data}
        emptyMessage="조건에 맞는 검수 기록이 없습니다."
      />

      {data && data.pagination.totalPages > 1 && (
        <div className={refStyles.paginationWrap}>
          <Pagination
            current={data.pagination.page}
            total={data.pagination.totalPages}
            onChange={(page) => setFilters({ page })}
          />
        </div>
      )}

      {/* 4. 지표별 값 분포 */}
      <div className={styles.distSection}>
        <h2 className={styles.distTitle}>지표별 값 분포</h2>
        <p className={styles.distHint}>
          현재 필터 조건의 전체 검수 기록 기준. 값이 어디 몰려 있는지 보고 위 "임계값 설정" 탭에서 warn/block 경계를 조정하세요.
        </p>
        <div className={styles.distGrid}>
          {METRIC_CHARTS.map((mc) => {
            const bins = data?.distribution?.[mc.key] ?? []
            const hasData = bins.some((b) => b.value > 0)
            return (
              <div key={mc.key} className={styles.distCard}>
                <div className={styles.distCardHeader}>
                  <span className={styles.distCardTitle}>{mc.label}</span>
                  <span className={styles.distCardBounds}>{mc.bounds(thresholds)}</span>
                </div>
                {hasData ? (
                  <BarChart data={bins} showTooltip formatTooltip={(d) => `${d.label}: ${d.value}건`} />
                ) : (
                  <div className={styles.distEmpty}>데이터 없음</div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* 열람 사유 입력 */}
      <ViewReasonModal
        open={!!reasonTarget}
        onClose={() => { setReasonTarget(null); viewMutation.reset() }}
        onConfirm={handleConfirmView}
        title={reasonTarget ? `${reasonTarget.label} 참조사진 열람` : '열람 사유 입력'}
        description="얼굴 사진(생체정보) 열람은 접속기록으로 남습니다. 열람 사유를 입력해 주세요."
        loading={viewMutation.isLoading}
        error={viewMutation.error}
      />

      {/* 라이트박스 */}
      <Modal
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        size="xl"
        title={lightbox ? `${lightbox.label} (${lightbox.index + 1}/${lightbox.urls.length})` : undefined}
      >
        {lightbox && (
          <div className={refStyles.lightbox}>
            <button
              type="button"
              className={refStyles.lightboxNav}
              onClick={() => setLightbox({ ...lightbox, index: (lightbox.index - 1 + lightbox.urls.length) % lightbox.urls.length })}
              aria-label="이전"
              disabled={lightbox.urls.length < 2}
            >
              <ChevronLeft size={24} />
            </button>
            <img src={lightbox.urls[lightbox.index]} alt={`${lightbox.label} ${lightbox.index + 1}`} className={refStyles.lightboxImg} />
            <button
              type="button"
              className={refStyles.lightboxNav}
              onClick={() => setLightbox({ ...lightbox, index: (lightbox.index + 1) % lightbox.urls.length })}
              aria-label="다음"
              disabled={lightbox.urls.length < 2}
            >
              <ChevronRight size={24} />
            </button>
          </div>
        )}
        {lightbox && (
          <div className={refStyles.lightboxFooter}>
            <a href={lightbox.urls[lightbox.index]} target="_blank" rel="noopener noreferrer" className={refStyles.lightboxLink}>
              원본 열기
            </a>
          </div>
        )}
      </Modal>
    </div>
  )
}
